import { useState, useEffect, useCallback, useContext } from 'react';
import useHttp from './useHttp';
import { toast } from 'react-toastify';
import AuthContext from '../context/authContext';

const useAutoAnnouncements = entertainmentId => {
  const authCtx = useContext(AuthContext);
  const { sendRequest, isLoading } = useHttp();
  const [config, setConfig] = useState({
    mode: null,
    briefing: {
      enabled: false,
      totalSecondsBefore: 180,
      defaultSecondsBefore: 180,
    },
    gameStart: {
      enabled: false,
      totalSecondsBefore: 60,
      defaultSecondsBefore: 60,
    },
  });

  const loadConfig = useCallback(async () => {
    if (!entertainmentId) return;

    try {
      const response = await sendRequest(
        {
          url: `/api/v1/lasertag-games/auto-announcements/${entertainmentId}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        },
        null,
        null,
        null,
        null,
        null
      );
      if (response) {
        setConfig(response);
      }
    } catch (err) {
      // Silent fail - use defaults
      console.error('Failed to load auto-announcements config:', err);
    }
  }, [entertainmentId]);

  useEffect(() => {
    if (!entertainmentId || !authCtx.checkRight('lasertagGame:read')) return;
    loadConfig();
  }, [entertainmentId]);

  const updateConfig = useCallback(
    async (updates, showToast = true) => {
      if (!entertainmentId) return;

      try {
        const response = await sendRequest(
          {
            url: `/api/v1/lasertag-games/auto-announcements/${entertainmentId}`,
            method: 'POST',
            body: updates,
            headers: authCtx.getAuthHeaders(),
          },
          null,
          null,
          null,
          null,
          null
        );

        if (response && response.config) {
          setConfig(response.config);
        } else {
          // Reload to get updated state
          await loadConfig();
        }

        if (showToast) {
          toast.success('Einstellungen gespeichert');
        }
      } catch (err) {
        if (showToast) {
          toast.error('Fehler beim Speichern der Einstellungen');
        }
        throw err;
      }
    },
    [entertainmentId]
  );

  const skipAnnouncement = useCallback(
    async (type, gameId) => {
      if (!entertainmentId || !gameId) return;

      try {
        await sendRequest(
          {
            url: `/api/v1/lasertag-games/auto-announcements/${entertainmentId}/skip`,
            method: 'POST',
            body: { type, gameId },
            headers: authCtx.getAuthHeaders(),
          },
          null,
          null,
          null,
          null,
          null
        );
        // Update local state to mark as triggered
        setConfig(prev => ({
          ...prev,
          [type]: {
            ...prev[type],
            triggeredForGameId: gameId,
          },
        }));
      } catch (err) {
        toast.error('Fehler beim Überspringen');
        throw err;
      }
    },
    [entertainmentId]
  );

  return {
    config,
    isLoading,
    updateConfig,
    skipAnnouncement,
    reloadConfig: loadConfig,
  };
};

export default useAutoAnnouncements;
